import { h } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const DEFAULT_CENTER = [2.2137, 46.2276];
const DEFAULT_MARKER_URL = "/images/bk_loc_tiny.png";
const DEFAULT_ENDPOINT = "/anon/ambassadors/map";
const DEFAULT_STYLE = "mapbox://styles/mapbox/streets-v12";
const EXTERNAL_MODE_VALUE = "O";
const HOVER_CLOSE_DELAY = 420;
const MAPBOX_GEOCODING_BATCH_URL = "https://api.mapbox.com/search/geocode/v6/batch";
const MAPBOX_GEOCODING_BATCH_SIZE = 50;
const AMBASSADORS_SOURCE_ID = "bk-ambassadors-source";
const AMBASSADORS_LAYER_ID = "bk-ambassadors-markers";
const AMBASSADORS_IMAGE_ID = "bk-ambassador-marker";

export function AmbassadorsMap({
  accessToken,
  endpoint = DEFAULT_ENDPOINT,
  markerImageUrl = DEFAULT_MARKER_URL,
  mapStyle = DEFAULT_STYLE,
  initialCenter = DEFAULT_CENTER,
  initialZoom = 4,
  singleMarkerZoom = 11.5,
  fitBounds = false,
  maxFitZoom = 12,
  height = 520,
  geocodingCountry = "fr",
  questionHrefBuilder = defaultQuestionHrefBuilder,
  className,
  sx,
}) {
  const mapFrameRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const ambassadorLayerCleanupRef = useRef(null);

  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestError, setRequestError] = useState("");
  const [mapError, setMapError] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [externalMode, setExternalMode] = useState(readExternalMode);

  const resolvedAccessToken = useMemo(
    () => resolveAccessToken(accessToken),
    [accessToken]
  );

  const resolvedCenter = normalizeCenter(initialCenter);
  const centerKey = resolvedCenter.join(":");
  const resolvedHeight = typeof height === "number" ? `${height}px` : height;
  const resolvedInitialZoom = finiteNumber(initialZoom, 4);
  const resolvedSingleMarkerZoom = finiteNumber(singleMarkerZoom, 11.5);
  const resolvedMaxFitZoom = finiteNumber(maxFitZoom, 12);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncExternalMode = () => setExternalMode(readExternalMode());

    window.addEventListener("popstate", syncExternalMode);
    window.addEventListener("hashchange", syncExternalMode);

    return () => {
      window.removeEventListener("popstate", syncExternalMode);
      window.removeEventListener("hashchange", syncExternalMode);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    async function loadAmbassadors() {
      setLoading(true);
      setRequestError("");

      try {
        const response = await fetch(endpoint, {
          method: "GET",
          credentials: "same-origin",
          headers: {
            Accept: "application/geo+json, application/json",
          },
          signal: controller ? controller.signal : undefined,
        });

        if (!response.ok) {
          throw new Error(
            `Impossible de charger les ambassadeurs (HTTP ${response.status}).`
          );
        }

        const payload = await response.json();
        const addressableFeatures = normalizeFeatureCollection(payload);
        const nextFeatures = await geocodeMissingFeatures({
          features: addressableFeatures,
          accessToken: resolvedAccessToken,
          country: geocodingCountry,
          signal: controller ? controller.signal : undefined,
        });

        if (!cancelled) {
          setFeatures(nextFeatures);
        }
      } catch (error) {
        if (error && error.name === "AbortError") {
          return;
        }

        if (!cancelled) {
          setFeatures([]);
          setRequestError(
            error && error.message
              ? error.message
              : "Impossible de charger les ambassadeurs."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAmbassadors();

    return () => {
      cancelled = true;
      if (controller) {
        controller.abort();
      }
    };
  }, [endpoint, geocodingCountry, resolvedAccessToken]);

  useEffect(() => {
    if (!resolvedAccessToken || !mapContainerRef.current) {
      return undefined;
    }

    let map;

    setMapReady(false);
    setMapError("");

    try {
      mapboxgl.accessToken = resolvedAccessToken;

      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center: resolvedCenter,
        zoom: resolvedInitialZoom,
        attributionControl: true,
      });

      mapRef.current = map;
      map.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        "top-right"
      );

      const handleLoad = () => {
        setMapReady(true);
        setMapError("");
      };

      const handleError = (event) => {
        const message =
          event && event.error && event.error.message
            ? event.error.message
            : "";

        if (/token|unauthorized|forbidden|style/i.test(message)) {
          setMapError(message);
        }
      };

      map.on("load", handleLoad);
      map.on("error", handleError);

      return () => {
        map.off("load", handleLoad);
        map.off("error", handleError);
        if (typeof ambassadorLayerCleanupRef.current === "function") {
          ambassadorLayerCleanupRef.current();
          ambassadorLayerCleanupRef.current = null;
        }
        map.remove();
        mapRef.current = null;
      };
    } catch (error) {
      setMapError(
        error && error.message
          ? error.message
          : "MapBox n'a pas pu initialiser la carte."
      );
      mapRef.current = null;
      return undefined;
    }
  }, [resolvedAccessToken, mapStyle, centerKey, resolvedInitialZoom]);

  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapReady) {
      return undefined;
    }

    if (typeof ambassadorLayerCleanupRef.current === "function") {
      ambassadorLayerCleanupRef.current();
      ambassadorLayerCleanupRef.current = null;
    }

    if (features.length === 0) {
      removeAmbassadorsLayer(map);
      return undefined;
    }

    let cancelled = false;
    let interactionCleanup = null;
    const bounds = new mapboxgl.LngLatBounds();

    features.forEach((feature) => {
      bounds.extend(feature.geometry.coordinates);
    });

    const cleanup = () => {
      cancelled = true;

      if (typeof interactionCleanup === "function") {
        interactionCleanup();
        interactionCleanup = null;
      }

      removeAmbassadorsLayer(map);
    };

    ambassadorLayerCleanupRef.current = cleanup;

    loadMarkerImage(map, markerImageUrl, (error, image) => {
      if (cancelled || mapRef.current !== map) {
        return;
      }

      if (error || !image) {
        setMapError(
          error && error.message
            ? error.message
            : "Impossible de charger l'image des ambassadeurs."
        );
        return;
      }

      try {
        removeAmbassadorsLayer(map);

        map.addImage(AMBASSADORS_IMAGE_ID, image, { pixelRatio: 1 });
        map.addSource(AMBASSADORS_SOURCE_ID, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features,
          },
        });
        map.addLayer({
          id: AMBASSADORS_LAYER_ID,
          type: "symbol",
          source: AMBASSADORS_SOURCE_ID,
          layout: {
            "icon-image": AMBASSADORS_IMAGE_ID,
            "icon-anchor": "bottom",
            "icon-size": 1,
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          },
        });

        interactionCleanup = externalMode
          ? bindExternalLayerTooltip({
              map,
              layerId: AMBASSADORS_LAYER_ID,
              tooltipContainer: mapFrameRef.current,
              questionHrefBuilder,
            })
          : null;
      } catch (layerError) {
        setMapError(
          layerError && layerError.message
            ? layerError.message
            : "Impossible d'afficher les ambassadeurs sur la carte."
        );
      }
    });

    if (fitBounds && !bounds.isEmpty()) {
      if (features.length === 1) {
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: Math.min(resolvedSingleMarkerZoom, resolvedMaxFitZoom),
          duration: 500,
        });
      } else {
        map.fitBounds(bounds, {
          padding: { top: 56, right: 56, bottom: 56, left: 56 },
          maxZoom: resolvedMaxFitZoom,
          duration: 500,
        });
      }
    }

    return () => {
      cleanup();
      if (ambassadorLayerCleanupRef.current === cleanup) {
        ambassadorLayerCleanupRef.current = null;
      }
    };
  }, [
    externalMode,
    features,
    fitBounds,
    mapReady,
    markerImageUrl,
    questionHrefBuilder,
    resolvedMaxFitZoom,
    resolvedSingleMarkerZoom,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    const container = mapContainerRef.current;

    if (!map || !container || !mapReady) {
      return undefined;
    }

    const resize = () => map.resize();
    let observer;

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(resize);
      observer.observe(container);
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    resize();

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, [mapReady]);

  const tokenError = resolvedAccessToken
    ? ""
    : "Le token public MapBox est absent. Passez accessToken au composant ou exposez-le dans la configuration Bfront.";
  const visibleError = tokenError || requestError || mapError;

  return (
    <Box
      className={className}
      role="region"
      aria-label="Carte des ambassadeurs"
      sx={[
        {
          width: "100%",
          height: resolvedHeight,
          minHeight: "280px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        ref={mapFrameRef}
        sx={{
          position: "relative",
          width: resolvedHeight,
          height: resolvedHeight,
          flex: "0 0 auto",
          overflow: "visible",
          bgcolor: "grey.100",
        }}
      >
        <Box
          ref={mapContainerRef}
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderRadius: 2,
          }}
        />

        {!loading && !visibleError && (
          <Chip
            size="small"
            label={ambassadorCountLabel(features.length)}
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              bgcolor: "background.paper",
            }}
          />
        )}

        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.72)",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && visibleError && (
          <Box
            sx={{
              position: "absolute",
              left: 16,
              right: 16,
              bottom: 16,
              zIndex: 4,
            }}
          >
            <Alert severity="error">{visibleError}</Alert>
          </Box>
        )}

        {!loading && !visibleError && features.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1,
                px: 2,
                py: 1,
                boxShadow: 1,
              }}
            >
              Aucune adresse d'ambassadeur n'a pu être géocodée.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function readExternalMode() {
  if (typeof window === "undefined" || !window.location) {
    return false;
  }

  const params = new URLSearchParams(window.location.search || "");
  const ext = params.get("ext");

  return isNonEmptyString(ext) && ext.trim().toUpperCase() === EXTERNAL_MODE_VALUE;
}

function resolveAccessToken(explicitToken) {
  if (isNonEmptyString(explicitToken)) {
    return explicitToken.trim();
  }

  if (typeof window !== "undefined") {
    const config = window.__BK_CONFIG__ || {};
    const windowToken =
      config.mapboxAccessToken ||
      config.mapBoxAccessToken ||
      window.MAPBOX_ACCESS_TOKEN;

    if (isNonEmptyString(windowToken)) {
      return windowToken.trim();
    }
  }

  if (typeof document !== "undefined") {
    const meta = document.querySelector('meta[name="mapbox-access-token"]');
    if (meta && isNonEmptyString(meta.content)) {
      return meta.content.trim();
    }
  }

  if (
    typeof process !== "undefined" &&
    process.env &&
    isNonEmptyString(process.env.MAPBOX_ACCESS_TOKEN)
  ) {
    return process.env.MAPBOX_ACCESS_TOKEN.trim();
  }

  return "";
}

function normalizeFeatureCollection(payload) {
  const collection =
    payload && payload.type === "FeatureCollection"
      ? payload
      : payload && payload.data && payload.data.type === "FeatureCollection"
      ? payload.data
      : null;

  if (!collection || !Array.isArray(collection.features)) {
    throw new Error("La réponse des ambassadeurs n'est pas un GeoJSON valide.");
  }

  return collection.features
    .map(normalizeFeature)
    .filter((feature) => feature !== null);
}

function normalizeFeature(feature) {
  if (!feature) {
    return null;
  }

  const properties = feature.properties || {};
  const coordinates = normalizePointCoordinates(feature.geometry);

  if (coordinates) {
    return {
      ...feature,
      geometry: {
        type: "Point",
        coordinates,
      },
      properties,
    };
  }

  // Bback peut renvoyer geometry: null lorsque l'adresse est textuelle.
  // Elle sera géocodée par Mapbox avant la création des markers.
  if (isNonEmptyString(properties.address)) {
    return {
      ...feature,
      geometry: null,
      properties,
    };
  }

  return null;
}

function normalizePointCoordinates(geometry) {
  if (
    !geometry ||
    geometry.type !== "Point" ||
    !Array.isArray(geometry.coordinates) ||
    geometry.coordinates.length < 2
  ) {
    return null;
  }

  const longitude = Number(geometry.coordinates[0]);
  const latitude = Number(geometry.coordinates[1]);

  if (
    !Number.isFinite(longitude) ||
    !Number.isFinite(latitude) ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90
  ) {
    return null;
  }

  return [longitude, latitude];
}

async function geocodeMissingFeatures({
  features,
  accessToken,
  country,
  signal,
}) {
  const alreadyMapped = [];
  const waitingForCoordinates = [];

  (features || []).forEach((feature) => {
    if (normalizePointCoordinates(feature.geometry)) {
      alreadyMapped.push(feature);
    } else if (isNonEmptyString(feature.properties && feature.properties.address)) {
      waitingForCoordinates.push(feature);
    }
  });

  if (waitingForCoordinates.length === 0 || !isNonEmptyString(accessToken)) {
    return alreadyMapped;
  }

  const featuresByAddress = new Map();

  waitingForCoordinates.forEach((feature) => {
    const address = feature.properties.address.trim();
    const current = featuresByAddress.get(address) || [];
    current.push(feature);
    featuresByAddress.set(address, current);
  });

  const uniqueAddresses = Array.from(featuresByAddress.keys());
  const coordinatesByAddress = new Map();

  for (let index = 0; index < uniqueAddresses.length; index += MAPBOX_GEOCODING_BATCH_SIZE) {
    const addresses = uniqueAddresses.slice(index, index + MAPBOX_GEOCODING_BATCH_SIZE);
    const queries = addresses.map((address) => {
      const query = {
        q: address,
        limit: 1,
        autocomplete: false,
      };

      if (isNonEmptyString(country)) {
        query.country = country.trim().toLowerCase();
      }

      return query;
    });

    const response = await fetch(
      `${MAPBOX_GEOCODING_BATCH_URL}?access_token=${encodeURIComponent(accessToken.trim())}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(queries),
        signal,
      }
    );

    if (!response.ok) {
      throw new Error(
        `Impossible de géocoder les adresses des ambassadeurs (MapBox HTTP ${response.status}).`
      );
    }

    const payload = await response.json();
    const batch = payload && Array.isArray(payload.batch) ? payload.batch : [];

    addresses.forEach((address, batchIndex) => {
      const result = batch[batchIndex];
      const firstFeature =
        result && Array.isArray(result.features) ? result.features[0] : null;
      const coordinates = normalizePointCoordinates(
        firstFeature && firstFeature.geometry
      );

      if (coordinates) {
        coordinatesByAddress.set(address, coordinates);
      }
    });
  }

  const geocoded = [];

  featuresByAddress.forEach((addressFeatures, address) => {
    const coordinates = coordinatesByAddress.get(address);

    if (!coordinates) {
      return;
    }

    addressFeatures.forEach((feature) => {
      geocoded.push({
        ...feature,
        geometry: {
          type: "Point",
          coordinates,
        },
        properties: {
          ...(feature.properties || {}),
          geocoded: true,
        },
      });
    });
  });

  return alreadyMapped.concat(geocoded);
}

function normalizeCenter(center) {
  if (!Array.isArray(center) || center.length < 2) {
    return DEFAULT_CENTER;
  }

  const longitude = Number(center[0]);
  const latitude = Number(center[1]);

  if (
    !Number.isFinite(longitude) ||
    !Number.isFinite(latitude) ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90
  ) {
    return DEFAULT_CENTER;
  }

  return [longitude, latitude];
}

function loadMarkerImage(map, markerImageUrl, callback) {
  map.loadImage(markerImageUrl, (error, image) => {
    callback(error, image);
  });
}

function removeAmbassadorsLayer(map) {
  if (!map) {
    return;
  }

  try {
    if (map.getLayer(AMBASSADORS_LAYER_ID)) {
      map.removeLayer(AMBASSADORS_LAYER_ID);
    }

    if (map.getSource(AMBASSADORS_SOURCE_ID)) {
      map.removeSource(AMBASSADORS_SOURCE_ID);
    }

    if (map.hasImage(AMBASSADORS_IMAGE_ID)) {
      map.removeImage(AMBASSADORS_IMAGE_ID);
    }
  } catch (_error) {
    // La carte peut être en cours de destruction ; aucun nettoyage supplémentaire.
  }
}

function featureFromEvent(event) {
  return event && Array.isArray(event.features) && event.features.length > 0
    ? event.features[0]
    : null;
}

function coordinatesFromFeature(feature) {
  return normalizePointCoordinates(feature && feature.geometry);
}

function featureKey(feature) {
  if (!feature) {
    return "";
  }

  const properties = feature.properties || {};
  if (isNonEmptyString(properties.uid)) {
    return `uid:${properties.uid.trim()}`;
  }

  if (feature.id !== undefined && feature.id !== null) {
    return `id:${String(feature.id)}`;
  }

  const coordinates = coordinatesFromFeature(feature);
  return coordinates ? `coord:${coordinates.join(":")}` : "";
}

function bindExternalLayerTooltip({
  map,
  layerId,
  tooltipContainer,
  questionHrefBuilder,
}) {
  if (!tooltipContainer) {
    return () => {};
  }

  let activeFeatureKey = null;
  let activeCoordinates = null;
  let closeTimer = null;

  const tooltip = document.createElement("div");
  tooltip.setAttribute("role", "tooltip");
  tooltip.style.position = "absolute";
  tooltip.style.display = "none";
  tooltip.style.zIndex = "20";
  tooltip.style.minWidth = "180px";
  tooltip.style.maxWidth = "260px";
  tooltip.style.padding = "10px 12px";
  tooltip.style.background = "#ffffff";
  tooltip.style.color = "rgba(0, 0, 0, 0.87)";
  tooltip.style.borderRadius = "6px";
  tooltip.style.boxShadow = "0 3px 14px rgba(0, 0, 0, 0.22)";
  tooltip.style.fontFamily = "inherit";
  tooltip.style.fontSize = "14px";
  tooltip.style.lineHeight = "1.35";
  tooltip.style.pointerEvents = "auto";
  tooltip.style.transform = "translate(-50%, calc(-100% - 25px))";
  tooltip.style.willChange = "left, top";
  tooltip.style.boxSizing = "border-box";

  const cancelClose = () => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
  };

  const hideTooltip = () => {
    closeTimer = null;
    activeFeatureKey = null;
    activeCoordinates = null;
    tooltip.style.display = "none";
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer = window.setTimeout(hideTooltip, HOVER_CLOSE_DELAY);
  };

  const updatePosition = () => {
    if (!activeCoordinates || tooltip.style.display === "none") {
      return;
    }

    const point = map.project(activeCoordinates);
    tooltip.style.left = `${Math.round(point.x)}px`;
    tooltip.style.top = `${Math.round(point.y)}px`;
  };

  const showTooltip = (event) => {
    const feature = featureFromEvent(event);
    const coordinates = coordinatesFromFeature(feature);

    if (!feature || !coordinates) {
      return;
    }

    cancelClose();
    activeFeatureKey = featureKey(feature);
    activeCoordinates = coordinates;

    tooltip.replaceChildren(
      createExternalTooltipContent(
        feature.properties || {},
        questionHrefBuilder
      )
    );
    tooltip.style.display = "block";
    updatePosition();
  };

  const handleMouseEnter = (event) => {
    map.getCanvas().style.cursor = "pointer";
    showTooltip(event);
  };

  const handleMouseMove = (event) => {
    // Permet de changer proprement de marker lorsque plusieurs symboles se touchent,
    // sans recréer/supprimer un popup Mapbox à chaque événement.
    const feature = featureFromEvent(event);
    if (feature && featureKey(feature) !== activeFeatureKey) {
      showTooltip(event);
    }
  };

  const handleMouseLeave = () => {
    map.getCanvas().style.cursor = "";
    scheduleClose();
  };

  const handleClick = (event) => {
    showTooltip(event);
  };

  tooltip.addEventListener("pointerenter", cancelClose);
  tooltip.addEventListener("pointerleave", scheduleClose);
  tooltip.addEventListener("focusin", cancelClose);
  tooltip.addEventListener("focusout", scheduleClose);
  tooltipContainer.appendChild(tooltip);

  map.on("mouseenter", layerId, handleMouseEnter);
  map.on("mousemove", layerId, handleMouseMove);
  map.on("mouseleave", layerId, handleMouseLeave);
  map.on("click", layerId, handleClick);
  map.on("move", updatePosition);
  map.on("resize", updatePosition);

  return () => {
    cancelClose();
    map.off("mouseenter", layerId, handleMouseEnter);
    map.off("mousemove", layerId, handleMouseMove);
    map.off("mouseleave", layerId, handleMouseLeave);
    map.off("click", layerId, handleClick);
    map.off("move", updatePosition);
    map.off("resize", updatePosition);
    map.getCanvas().style.cursor = "";

    tooltip.removeEventListener("pointerenter", cancelClose);
    tooltip.removeEventListener("pointerleave", scheduleClose);
    tooltip.removeEventListener("focusin", cancelClose);
    tooltip.removeEventListener("focusout", scheduleClose);

    if (tooltip.parentNode) {
      tooltip.parentNode.removeChild(tooltip);
    }
  };
}

function createExternalTooltipContent(properties, questionHrefBuilder) {
  const root = document.createElement("div");
  root.style.minWidth = "0";
  root.style.maxWidth = "100%";

  appendPopupLine(
    root,
    properties.label || properties.name || "Ambassadeur",
    true
  );

  const href = buildQuestionHref(questionHrefBuilder, properties);

  if (href) {
    const link = document.createElement("a");
    link.href = href;
    link.textContent = "lui poser une question";
    link.style.display = "inline-block";
    link.style.marginTop = "6px";
    link.style.fontWeight = "600";
    link.style.color = "#1976d2";
    link.style.textDecoration = "underline";
    link.style.cursor = "pointer";
    link.addEventListener("click", (event) => event.stopPropagation());
    root.appendChild(link);
  }

  return root;
}

function buildQuestionHref(questionHrefBuilder, properties) {
  if (typeof questionHrefBuilder !== "function") {
    return "";
  }

  try {
    const href = questionHrefBuilder(properties.uid, properties);
    return isNonEmptyString(href) ? href.trim() : "";
  } catch (_error) {
    return "";
  }
}

function defaultQuestionHrefBuilder(uid) {
  if (!isNonEmptyString(uid)) {
    return "";
  }

  return `/contact?ext=O&ambassador_uid=${encodeURIComponent(uid.trim())}`;
}

function appendPopupLine(root, value, emphasized) {
  if (!isNonEmptyString(value)) {
    return;
  }

  const line = document.createElement("div");
  line.textContent = value.trim();
  line.style.margin = "2px 0";

  if (emphasized) {
    line.style.fontWeight = "600";
  }

  root.appendChild(line);
}

function ambassadorCountLabel(count) {
  return count === 1 ? "1 ambassadeur" : `${count} ambassadeurs`;
}

function finiteNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

export default AmbassadorsMap;
